import { Controller } from "@hotwired/stimulus";
import { closePermanentMapEventName } from "../PermanentMap/SmartGuidePermanentMapEvents";

type NavigationHistoryState = {
    entries: string[];
    currentIndex: number;
};

type NavigationHistoryRecorder = {
    recordPushStateUrl(url: string | URL | null | undefined): void;
    recordReplaceStateUrl(url: string | URL | null | undefined): void;
};

type TurboWindow = Window & {
    Turbo?: {
        visit(location: string): void;
    };
};

type NavigationWindow = Window & {
    navigation?: {
        canGoBack?: boolean;
    };
};

const controllers = new Set<NavigationHistoryRecorder>();
let originalPushState: typeof window.history.pushState | undefined;
let originalReplaceState: typeof window.history.replaceState | undefined;

function installHistoryPatch(controller: NavigationHistoryRecorder): void {
    controllers.add(controller);
    if (originalPushState && originalReplaceState) {
        return;
    }

    originalPushState = window.history.pushState;
    originalReplaceState = window.history.replaceState;

    window.history.pushState = function pushState(data: unknown, unused: string, url?: string | URL | null): void {
        originalPushState!.apply(window.history, [data, unused, url]);
        controllers.forEach(item => item.recordPushStateUrl(url));
    } as typeof window.history.pushState;

    window.history.replaceState = function replaceState(data: unknown, unused: string, url?: string | URL | null): void {
        originalReplaceState!.apply(window.history, [data, unused, url]);
        controllers.forEach(item => item.recordReplaceStateUrl(url));
    } as typeof window.history.replaceState;
}

function uninstallHistoryPatch(controller: NavigationHistoryRecorder): void {
    controllers.delete(controller);
    if (controllers.size > 0 || !originalPushState || !originalReplaceState) {
        return;
    }

    window.history.pushState = originalPushState;
    window.history.replaceState = originalReplaceState;
    originalPushState = undefined;
    originalReplaceState = undefined;
}

export default class SmartGuideNavigationHistoryController extends Controller<HTMLElement> implements NavigationHistoryRecorder {
    private suppressedSyntheticBackClickUntil = 0;

    connect(): void {
        this.initializeState();
        installHistoryPatch(this);
        document.addEventListener("pointerup", this.onDocumentPointerUp, true);
        document.addEventListener("click", this.onDocumentClick, true);
        window.addEventListener("popstate", this.onPopState);
    }

    disconnect(): void {
        document.removeEventListener("pointerup", this.onDocumentPointerUp, true);
        document.removeEventListener("click", this.onDocumentClick, true);
        window.removeEventListener("popstate", this.onPopState);
        uninstallHistoryPatch(this);
    }

    recordPushStateUrl(url: string | URL | null | undefined): void {
        const absoluteUrl = this.absoluteUrl(url);
        const state = this.readState() ?? this.createInitialState();
        const entries = state.entries.slice(0, state.currentIndex + 1);
        entries.push(absoluteUrl);
        this.writeState({
            entries,
            currentIndex: entries.length - 1
        });
    }

    recordReplaceStateUrl(url: string | URL | null | undefined): void {
        const absoluteUrl = this.absoluteUrl(url);
        const state = this.readState() ?? this.createInitialState();
        const entries = [...state.entries];
        entries[Math.max(0, state.currentIndex)] = absoluteUrl;
        this.writeState({
            entries,
            currentIndex: Math.max(0, state.currentIndex)
        });
    }

    private readonly onDocumentClick = (event: MouseEvent): void => {
        if (this.shouldSuppressSyntheticBackClick()) {
            const link = this.smartBackLinkFromEvent(event);
            if (link) {
                event.preventDefault();
                event.stopImmediatePropagation();
            }
            return;
        }

        this.handleSmartBackEvent(event);
    };

    private readonly onDocumentPointerUp = (event: PointerEvent): void => {
        if (event.pointerType !== "touch" && event.pointerType !== "pen") {
            return;
        }

        if (this.handleSmartBackEvent(event)) {
            this.suppressedSyntheticBackClickUntil = this.now() + 750;
        }
    };

    private readonly onPopState = (): void => {
        this.recordPopStateUrl(window.location.href);
    };

    private handleSmartBackEvent(event: Event): boolean {
        const link = this.smartBackLinkFromEvent(event);
        if (!link || !this.shouldHandleLinkEvent(event, link)) {
            return false;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (document.body.classList.contains("smartguide-map-visible")) {
            document.dispatchEvent(new CustomEvent(closePermanentMapEventName));
            return true;
        }

        const linkUrl = this.absoluteUrl(link.getAttribute("href") || link.href);
        const state = this.readState();

        if (state && state.currentIndex > 0) {
            const previousUrl = state.entries[state.currentIndex - 1];
            if (this.urlsMatchForSmartBack(previousUrl, linkUrl) ||
                this.urlPathMatchesSmartBackPrefix(previousUrl, link.dataset.smartguideNavigationBackPathPrefix) ||
                this.urlPathMatchesAnySmartBackPrefix(previousUrl, link.dataset.smartguideNavigationBackPathPrefixes)) {
                // popstate confirms the traversal; an ignored back must not advance our mirror.
                window.history.back();
                return true;
            }
        }

        const referrerUrl = document.referrer;
        if (referrerUrl && this.browserCanGoBack() &&
            (this.urlsMatchForSmartBack(referrerUrl, linkUrl) ||
                this.urlPathMatchesSmartBackPrefix(referrerUrl, link.dataset.smartguideNavigationBackPathPrefix) ||
                this.urlPathMatchesAnySmartBackPrefix(referrerUrl, link.dataset.smartguideNavigationBackPathPrefixes))) {
            window.history.back();
            return true;
        }

        this.visit(linkUrl);
        return true;
    }

    private shouldHandleLinkEvent(event: Event, link: HTMLAnchorElement): boolean {
        if (event.defaultPrevented ||
            link.hasAttribute("download") ||
            (link.target && link.target.toLowerCase() !== "_self")) {
            return false;
        }

        if (event instanceof MouseEvent &&
            (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
            return false;
        }

        const href = link.getAttribute("href");
        return Boolean(href) && href !== "#";
    }

    private smartBackLinkFromEvent(event: Event): HTMLAnchorElement | undefined {
        const target = event.target instanceof Element ? event.target : null;
        const link = target?.closest<HTMLAnchorElement>("[data-smartguide-navigation-back]") ?? undefined;
        if (!link) {
            return undefined;
        }

        return link.dataset.smartguideNavigationBack?.trim() === "true"
            ? link
            : undefined;
    }

    private shouldSuppressSyntheticBackClick(): boolean {
        if (this.suppressedSyntheticBackClickUntil === 0) {
            return false;
        }

        if (this.now() > this.suppressedSyntheticBackClickUntil) {
            this.suppressedSyntheticBackClickUntil = 0;
            return false;
        }

        this.suppressedSyntheticBackClickUntil = 0;
        return true;
    }

    private recordPopStateUrl(url: string): void {
        const state = this.readState() ?? this.createInitialState();
        const absoluteUrl = this.absoluteUrl(url);
        if (state.entries[state.currentIndex] === absoluteUrl) {
            return;
        }

        const previousIndex = state.currentIndex - 1;
        if (state.entries[previousIndex] === absoluteUrl) {
            this.writeState({ entries: state.entries, currentIndex: previousIndex });
            return;
        }

        const nextIndex = state.currentIndex + 1;
        if (state.entries[nextIndex] === absoluteUrl) {
            this.writeState({ entries: state.entries, currentIndex: nextIndex });
            return;
        }

        const nearestIndex = this.nearestEntryIndex(state.entries, absoluteUrl, state.currentIndex);
        if (nearestIndex >= 0) {
            this.writeState({ entries: state.entries, currentIndex: nearestIndex });
            return;
        }

        this.writeState({
            entries: [absoluteUrl],
            currentIndex: 0
        });
    }

    private nearestEntryIndex(entries: string[], url: string, currentIndex: number): number {
        let nearestIndex = -1;
        let nearestDistance = Number.POSITIVE_INFINITY;
        entries.forEach((entry, index) => {
            if (entry !== url) {
                return;
            }

            const distance = Math.abs(index - currentIndex);
            if (distance < nearestDistance) {
                nearestDistance = distance;
                nearestIndex = index;
            }
        });

        return nearestIndex;
    }

    private initializeState(): void {
        const state = this.readState();
        if (!state) {
            this.writeState(this.createInitialState());
            return;
        }

        if (state.entries[state.currentIndex] !== window.location.href) {
            this.recordPopStateUrl(window.location.href);
        }
    }

    private createInitialState(): NavigationHistoryState {
        return {
            entries: [window.location.href],
            currentIndex: 0
        };
    }

    private readState(): NavigationHistoryState | undefined {
        try {
            const entries = JSON.parse(this.element.dataset.smartguideNavigationHistoryEntries ?? "[]") as unknown;
            const currentIndex = Number.parseInt(this.element.dataset.smartguideNavigationHistoryIndex ?? "-1", 10);
            if (!Array.isArray(entries) ||
                !entries.every(entry => typeof entry === "string") ||
                currentIndex < 0 ||
                currentIndex >= entries.length) {
                return undefined;
            }

            return {
                entries,
                currentIndex
            };
        } catch {
            return undefined;
        }
    }

    private writeState(state: NavigationHistoryState): void {
        this.element.dataset.smartguideNavigationHistoryEntries = JSON.stringify(state.entries);
        this.element.dataset.smartguideNavigationHistoryIndex = state.currentIndex.toString();
    }

    private absoluteUrl(url: string | URL | null | undefined): string {
        if (url === null || url === undefined) {
            return window.location.href;
        }

        return new URL(url.toString(), window.location.href).toString();
    }

    private urlsMatchForSmartBack(left: string, right: string): boolean {
        const leftUrl = new URL(left, window.location.href);
        const rightUrl = new URL(right, window.location.href);
        return leftUrl.origin === rightUrl.origin &&
            leftUrl.pathname === rightUrl.pathname &&
            leftUrl.search === rightUrl.search;
    }

    private urlPathMatchesSmartBackPrefix(url: string, pathPrefix: string | undefined): boolean {
        if (!pathPrefix) {
            return false;
        }

        const urlToCheck = new URL(url, window.location.href);
        const prefixUrl = new URL(pathPrefix, window.location.href);
        return urlToCheck.origin === prefixUrl.origin &&
            (urlToCheck.pathname === prefixUrl.pathname ||
                urlToCheck.pathname.startsWith(`${prefixUrl.pathname}/`));
    }

    private urlPathMatchesAnySmartBackPrefix(url: string, pathPrefixes: string | undefined): boolean {
        if (!pathPrefixes) {
            return false;
        }

        return pathPrefixes
            .split("|")
            .map(pathPrefix => pathPrefix.trim())
            .filter(pathPrefix => pathPrefix.length > 0)
            .some(pathPrefix => this.urlPathMatchesSmartBackPrefix(url, pathPrefix));
    }

    private visit(url: string): void {
        const turbo = (window as TurboWindow).Turbo;
        if (turbo) {
            turbo.visit(url);
            return;
        }

        window.location.assign(url);
    }

    private browserCanGoBack(): boolean {
        const canGoBack = (window as NavigationWindow).navigation?.canGoBack;
        return typeof canGoBack === "boolean"
            ? canGoBack
            : window.history.length > 1;
    }

    private now(): number {
        return window.performance?.now() ?? Date.now();
    }
}
